const STALE_AFTER_MS = 24 * 60 * 60 * 1000;

export interface RiskSignal {
  id: string;
  title: string;
  summary: string;
  analysis: string;
  imageUrl?: string | null;
  imageSourceUrl?: string | null;
  imageUnavailableReason?: string | null;
  imageGeneratedAt?: string | null;
  interpretation?: Partial<RiskSignalInterpretation> | null;
  degradationReason?: string | null;
  freshnessDiagnostics?: string | null;
  direction?: "bullish" | "bearish" | "neutral";
  refinementStatus?: "agentic" | "pending-refinement";
  score: number;
  severity: "critical" | "high" | "medium" | "low";
  source: "bulletin" | "catalyst-watch" | "risk-detector";
  relatedHeadlines: string[];
  narrativeThreads: string[];
  generatedAt: string;
}

export interface RiskSignalInterpretation {
  whatChanged: string;
  whyItMattersNow: string;
  headwindTailwindRead: string;
  whoLikelyDoesNotKnow: string;
  pricedInRead: string;
  deskImplication: string;
  invalidatesIf: string;
}

export interface RiskSignalPayload {
  signals: RiskSignal[];
  staleSignals?: RiskSignal[];
  generatedAt?: string;
  cached?: boolean;
  stale?: boolean;
  freshnessStatus?: "fresh" | "empty" | "stale-cache" | "generation-error";
  inputCounts?: { bulletins: number; catalysts: number };
  diagnostics?: { staleGeneratedAt?: string | null; reason?: string };
}

export interface CachedSignals {
  signals: RiskSignal[];
  generatedAt: string | null;
  stale: boolean;
  freshnessStatus: RiskSignalPayload["freshnessStatus"];
  inputCounts?: RiskSignalPayload["inputCounts"];
  diagnostics?: RiskSignalPayload["diagnostics"];
}

export const SOURCE_LABEL: Record<string, string> = {
  bulletin: "Bulletin",
  "catalyst-watch": "Catalyst",
  "risk-detector": "Systemic",
};

export function scoreColor(score: number): string {
  if (score >= 8) return "var(--fintheon-bearish)";
  if (score >= 6) return "#f97316";
  if (score >= 4) return "var(--fintheon-accent)";
  return "var(--fintheon-muted)";
}

export function isFreshGenerated(generatedAt: string | null | undefined) {
  if (!generatedAt) return false;
  const generatedMs = new Date(generatedAt).getTime();
  return (
    Number.isFinite(generatedMs) && Date.now() - generatedMs < STALE_AFTER_MS
  );
}

export function formatAge(generatedAt: string | null | undefined): string {
  if (!generatedAt) return "unknown";
  const hours = Math.max(
    0,
    (Date.now() - new Date(generatedAt).getTime()) / 3_600_000,
  );
  if (hours < 1) return "just now";
  if (hours < 24) return `${Math.round(hours)}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export function emptyCachedSignals(): CachedSignals {
  return {
    signals: [],
    generatedAt: null,
    stale: false,
    freshnessStatus: "empty",
  };
}

export function buildRiskSignalInterpretation(
  signal: RiskSignal,
): RiskSignalInterpretation {
  const supplied = signal.interpretation ?? {};
  const direction = signal.direction ?? "neutral";
  const source = SOURCE_LABEL[signal.source] ?? "RiskFlow";
  const headlines = signal.relatedHeadlines.slice(0, 2).join(" | ");
  const baseSummary = signal.summary || signal.analysis;
  const impact =
    direction === "bullish"
      ? "tailwind"
      : direction === "bearish"
        ? "headwind"
        : "mixed read";

  return {
    whatChanged:
      supplied.whatChanged ??
      `${source} promoted this into a ${signal.score.toFixed(1)} risk signal from the current desk window.`,
    whyItMattersNow:
      supplied.whyItMattersNow ??
      `${baseSummary} The timing matters because the signal is competing with current session positioning, not just background news.`,
    headwindTailwindRead:
      supplied.headwindTailwindRead ??
      `Current read: ${impact}. Treat follow-through as conditional on the next liquidity and volatility response.`,
    whoLikelyDoesNotKnow:
      supplied.whoLikelyDoesNotKnow ??
      "Late allocators and headline-only participants may not have connected this to the active desk window yet.",
    pricedInRead:
      supplied.pricedInRead ??
      (signal.score >= 7
        ? "Partly priced, but the second-order path is still live."
        : "Not fully priced unless the related headlines have already moved index beta."),
    deskImplication:
      supplied.deskImplication ??
      "Keep this attached to the desk read, watch for confirmation, and avoid treating it as an isolated headline.",
    invalidatesIf:
      supplied.invalidatesIf ??
      (headlines
        ? `Invalidates if the related headline cluster fades: ${headlines}.`
        : "Invalidates if price, vol, and follow-on headlines fail to confirm during the active window."),
  };
}

export function imageUnavailableCopy(signal: RiskSignal): string {
  if (signal.imageUrl) return "";
  return (
    signal.imageUnavailableReason ??
    "Image enrichment pending or unavailable for this Risk Signal."
  );
}
